import { MouseEvent, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    InputAdornment,
    Link,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import { currencyExact } from './format';
import StatusChip from './StatusChip';
import { Invoice } from './types';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import NavigationSearchIcon from 'modules/common/components/Icons/NavigationSearchIcon';
import MailIcon from 'modules/common/components/Icons/MailIcon';
import PlusIcon from 'modules/common/components/Icons/PlusIcon';

type FilterType = 'active' | 'all' | 'paid' | 'overdue' | 'draft';

const stopPropagation = (e: MouseEvent) => e.stopPropagation();

function InvoiceRow({ invoice }: Readonly<{ invoice: Invoice }>) {
    const history = useHistory();
    const path = `/bookkeeping/invoicing/${invoice.id}`;
    const openInvoice = () => history.push(path);

    return (
        <Box
            onClick={openInvoice}
            sx={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 1fr 1.5fr 1fr 1fr 100px 60px',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}>
            <Checkbox
                size="small"
                onClick={stopPropagation}
                sx={{ p: 0.5 }}
                inputProps={{ 'aria-label': `Select invoice ${invoice.number}` }}
            />
            <Typography variant="body2">{dayjs(invoice.date).format('MMM D, YYYY')}</Typography>
            <Link component={RouterLink} to={path} variant="body2" onClick={stopPropagation}>
                {invoice.number}
            </Link>
            <Typography variant="body2">{invoice.client}</Typography>
            <Typography variant="body2">{dayjs(invoice.due).format('MMM D, YYYY')}</Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {currencyExact(invoice.amount)}
            </Typography>
            <Box>
                <StatusChip status={invoice.status} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                ⋯
            </Typography>
        </Box>
    );
}

function InlineDropdown<T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: { value: T; label: string }[];
    onChange: (v: T) => void;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const current = options.find((o) => o.value === value);
    return (
        <>
            <Button
                variant="text"
                size="small"
                color="inherit"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<ChevronDownIcon fontSize="small" />}
                sx={{
                    color: 'text.primary',
                    fontWeight: 400,
                    px: 1,
                    py: 0.25,
                    minWidth: 0,
                    '&:hover': { bgcolor: 'action.hover' },
                }}>
                <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                    {label}:
                </Typography>
                <Typography component="span" variant="body2">
                    {current?.label}
                </Typography>
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {options.map((o) => (
                    <MenuItem
                        key={o.value}
                        selected={o.value === value}
                        onClick={() => {
                            onChange(o.value);
                            setAnchorEl(null);
                        }}>
                        {o.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default function InvoiceListCard({ invoices }: { invoices: Invoice[] }) {
    const history = useHistory();
    const [filterType, setFilterType] = useState<FilterType>('active');
    const [filterDate, setFilterDate] = useState<'all' | 'ytd' | '30d' | '90d'>('all');
    const [rows, setRows] = useState<10 | 25 | 50>(10);
    const [search, setSearch] = useState('');

    const now = dayjs();
    const visible = invoices.filter((inv) => {
        const invDate = dayjs(inv.date);
        if (filterDate === 'ytd' && invDate.year() !== now.year()) {
            return false;
        }
        if (filterDate === '30d' && invDate.isBefore(now.subtract(30, 'day'), 'day')) {
            return false;
        }
        if (filterDate === '90d' && invDate.isBefore(now.subtract(90, 'day'), 'day')) {
            return false;
        }
        if (filterType === 'active' && inv.status !== 'unpaid' && inv.status !== 'overdue') {
            return false;
        }
        if (filterType === 'paid' && inv.status !== 'paid') {
            return false;
        }
        if (filterType === 'draft' && inv.status !== 'draft') {
            return false;
        }
        if (filterType === 'overdue' && inv.status !== 'overdue') {
            return false;
        }
        const q = search.trim().toLowerCase();
        if (!q) {
            return true;
        }
        return (
            inv.number.toLowerCase().includes(q) ||
            inv.client.toLowerCase().includes(q) ||
            inv.status.toLowerCase().includes(q) ||
            String(inv.amount).includes(q)
        );
    });

    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', p: 3 }}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ mb: 2 }}>
                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                    <InlineDropdown
                        label="Type"
                        value={filterType}
                        options={[
                            { value: 'active', label: 'Active' },
                            { value: 'all', label: 'All' },
                            { value: 'paid', label: 'Paid' },
                            { value: 'overdue', label: 'Overdue' },
                            { value: 'draft', label: 'Draft' },
                        ]}
                        onChange={(v) => setFilterType(v as FilterType)}
                    />
                    <InlineDropdown
                        label="Date"
                        value={filterDate}
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'ytd', label: 'YTD' },
                            { value: '30d', label: 'Last 30 days' },
                            { value: '90d', label: 'Last 90 days' },
                        ]}
                        onChange={(v) => setFilterDate(v as typeof filterDate)}
                    />
                    <InlineDropdown
                        label="Rows"
                        value={String(rows)}
                        options={[
                            { value: '10', label: '10' },
                            { value: '25', label: '25' },
                            { value: '50', label: '50' },
                        ]}
                        onChange={(v) => setRows(Number(v) as 10 | 25 | 50)}
                    />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Search Invoices"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <NavigationSearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            sx: { bgcolor: 'background.paper' },
                        }}
                        sx={{ minWidth: 240 }}
                    />
                    <Button variant="outlined" color="primary" startIcon={<MailIcon fontSize="small" />}>
                        Aging Report
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PlusIcon fontSize="small" />}
                        onClick={() => history.push('/bookkeeping/invoicing/new')}>
                        New Invoice
                    </Button>
                </Stack>
            </Stack>
            <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr 1fr 1.5fr 1fr 1fr 100px 60px',
                        alignItems: 'center',
                        px: 2,
                        py: 1.5,
                    }}>
                    <Checkbox size="small" sx={{ p: 0.5 }} inputProps={{ 'aria-label': 'Select all invoices' }} />
                    <Typography variant="caption" fontWeight={700}>
                        Date
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                        Number
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                        Client
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                        Due
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                        Amount
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                        Status
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ textAlign: 'right' }}>
                        Action
                    </Typography>
                </Box>
                {visible.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="h6">No data.</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please try another filter.
                        </Typography>
                    </Box>
                ) : (
                    visible.slice(0, rows).map((inv) => <InvoiceRow key={inv.id} invoice={inv} />)
                )}
            </Box>
        </Box>
    );
}
